import React from "react";
import { motion } from "framer-motion";
import { Leaf, Droplets, Sun, Wind, Heart } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { IMAGES } from "../constants/kalorenceData";
import { Reveal } from "../components/Reveal";

const CARE_TIPS = [
  {
    icon: Droplets,
    title: "Hand wash, always",
    text: "Use cold water and a mild, pH-neutral soap. Never machine wash or wring — gently press the water out.",
  },
  {
    icon: Sun,
    title: "Dry in the shade",
    text: "Direct sunlight fades natural pigments. Dry flat or on a line in a cool, shaded breeze.",
  },
  {
    icon: Wind,
    title: "Iron on low",
    text: "Press on the reverse side with a low setting. Steam helps relax the natural fibres beautifully.",
  },
  {
    icon: Heart,
    title: "Store with love",
    text: "Fold loosely and store away from damp. A sprig of dried neem keeps the moths at bay, naturally.",
  },
];

const FAQS = [
  {
    q: "Will the botanical prints fade over time?",
    a: "Our colours are locked using traditional mineral mordanting, so they hold remarkably well. Like all natural dyes, they soften gracefully over years — many wearers find this gentle ageing the most beautiful part. Avoiding direct sun and harsh detergents keeps them vivid for decades.",
  },
  {
    q: "Why does my piece look different from the photo?",
    a: "Because it is genuinely one of a kind. Each garment is pressed with real leaves on a single day, so the exact placement, depth, and tone can never be repeated. The photo shows the spirit of the print — your piece is its own unrepeatable moment.",
  },
  {
    q: "Are the dyes really 100% natural?",
    a: "Yes. Every colour is drawn from plants, bark, roots, and minerals — eucalyptus, pomegranate skins, madder root, marigolds, and iron-rich earth. No synthetic dyes, mordants, or chemical fixers enter our process or our soil.",
  },
  {
    q: "How should I wash my dupatta or stole for the first time?",
    a: "Soak briefly in cold water with a pinch of salt to set the colour, then hand wash gently with mild soap. Rinse, press out the water without wringing, and dry flat in the shade. After the first wash, normal gentle hand washing is perfect.",
  },
  {
    q: "Can I return a piece if I change my mind?",
    a: "Since every garment is handcrafted and entirely unique, we craft in very small batches. Reach out within 7 days of delivery and we'll happily help with an exchange for unworn, unwashed pieces. Write to us at kalorence2022@gmail.com.",
  },
  {
    q: "Do you ship outside Bangalore and India?",
    a: "Yes — we ship across India and to select international destinations. Each order is packed by hand in our Jayanagar studio. Drop us a note for international shipping rates and timelines.",
  },
];

export const CareJournal = () => {
  return (
    <div data-testid="care-journal-page" className="bg-[#F7F5F0]">
      {/* Hero */}
      <section className="relative h-[55vh] min-h-[400px] flex items-end overflow-hidden">
        <img
          src={IMAGES.img_27}
          alt="Petal impressions on fabric"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2B3A2F]/90 via-[#2B3A2F]/40 to-[#2B3A2F]/20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pb-14 text-[#F7F5F0]">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="text-xs tracking-[0.25em] uppercase font-semibold text-[#E2DACF] mb-3 flex items-center gap-2">
              <Leaf className="w-4 h-4 text-[#C47D65]" /> Care Journal
            </p>
            <h1 className="font-cormorant text-5xl sm:text-6xl font-light leading-tight">
              Tending to living cloth
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Care tips grid */}
      <section className="py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-base text-[#5C6B5F] font-light leading-relaxed max-w-2xl mb-14">
              A Kalorence piece is not printed by machine — it carries the memory of real
              leaves. Treated with a little tenderness, it will age like a good story.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {CARE_TIPS.map((tip, i) => (
              <Reveal key={tip.title} delay={i * 0.1}>
                <div className="space-y-3">
                  <div className="w-12 h-12 bg-[#E2DACF] flex items-center justify-center">
                    <tip.icon className="w-5 h-5 text-[#2B3A2F]" />
                  </div>
                  <h3 className="font-cormorant text-2xl font-normal">{tip.title}</h3>
                  <p className="text-sm text-[#5C6B5F] font-light leading-relaxed">
                    {tip.text}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="bg-[#EDEAE2] py-20 sm:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="mb-12">
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#C47D65]">
                Gentle Questions
              </p>
              <h2 className="font-cormorant text-4xl sm:text-5xl font-light mt-3">
                Everything you may wonder
              </h2>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {FAQS.map((faq, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  data-testid={`faq-item-${i}`}
                  className="border-b border-[#2B3A2F]/15"
                >
                  <AccordionTrigger className="text-left font-cormorant text-xl sm:text-2xl font-normal hover:text-[#C47D65] hover:no-underline py-6">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-base text-[#5C6B5F] font-light leading-relaxed pb-6">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>
    </div>
  );
};
